/* eslint-disable @angular-eslint/no-input-rename */
/* eslint-disable @angular-eslint/no-output-rename */
import {
    AfterViewInit,
    ChangeDetectorRef,
    ContentChild,
    Directive,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewRef
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { OnChange } from '@df/ng/on-change';
import { TimeoutService } from '@df/ng/timeout.service';
import { Carousel } from '@df/ui-carousel/carousel.interface';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { Media } from '@df/ui/display/media/interfaces';
import { Subscription, merge } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { RequestService } from '@df/api/request/request.service';

/** Subset of the Network Information API we rely on. */
interface INetworkInformation {
    saveData?: boolean;
}

@Directive()
export abstract class AbstractPlayerComponent<T = any, E = any> implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    /** @internal */
    protected componentId = 'player';

    /** @internal Player instance */
    protected player: any;

    // status
    isPlaying: boolean = false;
    isPaused: boolean = false;
    isMuted: boolean = false;

    // If pause show play button
    showControl = false;

    /** ID */
    protected _id!: string;
    set id(value: string) {
        this._id = value;
    }
    get id(): string {
        return this._id;
    }

    @ContentChild('playButton', { static: false })
    playButton?: TemplateRef<any>;

    @ContentChild('pauseButton', { static: false })
    pauseButton?: TemplateRef<any>;

    @ContentChild('customTpl', { static: false })
    customTpl?: TemplateRef<any>;

    /**
     * Video URL.
     *
     * @type {string}
     */
    @Input('video-url')
    @OnChange<string>(function (this: AbstractPlayerComponent, value: string) {
        this.id = value;
    })
    videoUrl?: string;

    /**
     * Video player ID
     * @type {string}
     */
    @Input('video-id')
    @OnChange<string>(function (this: AbstractPlayerComponent, value: string) {
        this.id = value;
    })
    videoId?: string;

    /**
     * Start video onload
     * @type {boolean}
     */
    @Input()
    autoplay?: boolean;

    /**
     * Autoplay on vieport flag.
     *
     * @type {boolean}
     */
    @Input('autoplay-in-viewport')
    autoplayInViewport?: boolean;

    /**
     * Loop flag.
     *
     * @type {boolean}
     */
    @Input()
    loop?: boolean;

    /**
     * Use custom controls flag.
     *
     * @type {boolean}
     */
    @Input('use-custom-controls')
    useCustomControls?: boolean;

    /**
     * Carousel ID.
     *
     * @type {number | string}
     */
    @Input()
    carouselId?: number | string;

    /**
     * Carousel slide index.
     *
     * @type {number}
     */
    @Input()
    carouselIndex?: number;

    /**
     * Reset video to beginning when pausing.
     *
     * @type {boolean}
     */
    @Input('reset-on-pause')
    resetOnPause?: boolean;

    /**
     * Auto play in view after pause.
     * If `false` then the video will not autoplay when it will back in view.
     *
     * @type {boolean}
     */
    @Input('autoplay-on-pause')
    autoplayOnPause = true;

    /**
     * Player id
     * used to stop, play or pause from outside
     */
    @Input()
    playerIdentifier?: string;

    @Input()
    forceLoad?: boolean;

    @Output('onPlayerInit')
    playerInit = new EventEmitter<T>();

    @Output('onPlayerPlay')
    playerPlay = new EventEmitter<T>();

    @Output('onPlayerPause')
    playerPause = new EventEmitter<T>();

    @Output('onPlayerStop')
    playerStop = new EventEmitter<T>();

    @Output('onPlayerChange')
    playerChange = new EventEmitter<T>();

    @Output('onPlayerProgress')
    playerProgress = new EventEmitter<E>();

    protected subscriptions = new Subscription();

    protected initialized = false;
    /**
     * Trigger event when player is in or out of viewport.
     *
     * @param visible {boolean}
     */
    protected onInViewport?: (visible?: boolean) => void;

    private initiated = false;

    /**
     * Injected as a field so the constructor signature stays backwards compatible for subclasses.
     */
    protected readonly client = inject(Client);

    constructor(
        protected elementRef: ElementRef,
        protected domSanitizer: DomSanitizer,
        protected visibilityService: VisibilityService,
        protected changeDetectionRef: ChangeDetectorRef,
        protected timeoutService: TimeoutService,
        protected app: App,
        protected events: Events,
        protected requestService: RequestService
    ) {
        changeDetectionRef.detach();
    }

    ngOnInit(): void {
        this.onInViewport = this.timeoutService.throttle((visible?: boolean): void => {
            if (visible && this.canPlay()) {
                this.play(true);
            } else if (!this.autoplay || this.carouselId) {
                this.pause(true);
            }
        }, 10);

        // only set up when in viewport
        this.subscriptions.add(
            this.visibilityService
                .isInView(this.elementRef, { rootMargin: '300px' })
                .pipe(
                    filter<boolean>(visible => visible),
                    take(1)
                )
                .subscribe(() => this.load())
        );

        this.subscriptions.add(
            merge(this.playerInit, this.playerPlay, this.playerPause, this.playerStop).subscribe((event: T) =>
                this.playerChange.emit(event)
            )
        );

        this.initiated = true;

        this.detectChanges();

        this.update();

        if (this.forceLoad) {
            this.load();
        }
    }

    ngOnChanges(): void {
        if (this.initiated) {
            this.update();
        }
        this.detectChanges();
    }

    ngAfterViewInit(): void {
        if (this.autoplayInViewport !== null) {
            this.subscriptions.add(
                this.visibilityService
                    .isInView(this.elementRef)
                    .subscribe((visible: boolean) => this.onInViewport && this.onInViewport(visible))
            );
        }
    }

    /**
     * Do not remove!
     */
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get playerInstance(): any {
        return this.player;
    }

    /**
     * Any initial thing here
     */
    protected load() {
        this.initialized = true;
    }

    protected update(): void {}

    play(inView = false): void {}

    pause(inView = false): void {}

    stop(): void {}

    mute(): void {}

    unMute(): void {}

    protected checkInView(): void {
        this.visibilityService
            .isInView(this.elementRef)
            .pipe(take(1))
            .subscribe((visible: boolean) => this.onInViewport && this.onInViewport(visible));
    }

    /**
     * Whether the visitor asked for reduced motion or reduced data usage, or is on a phone.
     *
     * On size S a background video costs several MB and repaints the viewport for its whole
     * length, which is why it is not loaded there at all and the poster is kept instead.
     *
     * @returns {boolean}
     */
    protected get prefersNoAutoplay(): boolean {
        if (!this.app.isPlatformBrowser) {
            return false;
        }

        const connection = (navigator as Navigator & { connection?: INetworkInformation }).connection;

        return (
            this.client.isS || !!connection?.saveData || !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        );
    }

    /**
     * If autoplay in viewport or autoplay.
     *
     * @returns {boolean}
     */
    protected canPlay(): boolean {
        return !!(
            !!(this.autoplayInViewport || this.autoplay) &&
            this.autoplayInViewport &&
            ((this.autoplayOnPause && (this.isPaused === undefined || this.isPaused === true)) ||
                (!this.autoplayOnPause && this.isPaused === undefined))
        );
    }

    /**
     * Set custom controls.
     */
    protected setControlsListeners() {
        this.subscriptions.add(
            this.events.on(`${this.componentId}.stop`).subscribe((id?: number | string, url?: string) => this.onStop(id, url))
        );

        [Media.Events.play, Media.Events.pause, Media.Events.stop].forEach(event => {
            this.subscriptions.add(this.events.on(event).subscribe((playerId: string) => this.onControlsEvent(event, playerId)));
        });

        // Carousel control
        this.subscriptions.add(this.events.on('carousel.change').subscribe((data: Carousel.ChangeEvent) => this.onCarouselNext(data)));
    }

    private onControlsEvent(event: Media.Events, playerId?: string) {
        if (playerId && this.playerIdentifier === playerId) {
            switch (event) {
                case Media.Events.pause:
                    this.pause();
                    break;
                case Media.Events.play:
                    this.play();
                    break;
                case Media.Events.stop:
                    this.stop();
                    break;
            }
        }
    }

    /**
     * Trigger event when carousel slide change.
     */
    protected onCarouselNext(data: Carousel.ChangeEvent): void {
        if (this.carouselId && data && data.id && this.carouselId === data.id) {
            if (this.carouselIndex !== undefined && this.carouselIndex === data.index) {
                this.checkInView();
            } else {
                this.pause();
            }
        }
    }

    /**
     * Play video on `vimeo-player.play` event request.
     *
     * @param id
     * @param url
     */
    protected onPlay(id?: number | string, url?: string): void {
        if (id === this.videoId || this.videoUrl === url) {
            this.play();
        }
    }

    /**
     * Stop video on `vimeo-player.stop` event request.
     *
     * @param id
     * @param url
     */
    protected onStop(id?: number | string, url?: string): void {
        if (id === this.videoId || this.videoUrl === url) {
            this.pause();
        }
    }

    protected detectChanges(): void {
        if (this.initiated) {
            // dont make it async, requiers fixes in vimeo
            if (!(<ViewRef>this.changeDetectionRef).destroyed) {
                this.changeDetectionRef.detectChanges();
            }
        }
    }
}
